import { create } from 'zustand';
import { builderDispatch, PrismUI } from '../../_utils/ui';
import isCard from '../utils/is-card';
import { usePrismItemStore } from './usePrismItemStore';

export const cardFilters = [
  'baby',
  'birthday',
  'business',
  'christmas_and_holiday',
  'espanol_card',
  'everyday',
  'graduation',
  'licensed_character',
  'religious',
  'seasonal',
  'specialty_card',
  'thank_you_card',
  'wedding',
];

function productFiltersByDesignCode(products, code) {
  const product = products.find((p) => p.design_code === code);
  const filters = product ? product.filters : {};
  // Return the top-level key plus a compound "key:value" token for each
  // sub-value title, enabling two levels of specificity:
  // e.g. { seasonal: [{title:"easter"}] } → ["seasonal", "seasonal:easter"]
  return Object.entries(filters).flatMap(([key, values]) => {
    const subTokens = (Array.isArray(values) ? values : [])
      .map((v) => (v.title ? `${key}:${v.title.toLowerCase()}` : null))
      .filter(Boolean);
    return subTokens.length > 0 ? [key, ...subTokens] : [key];
  });
}

export const useThemesStore = create((set, get) => ({
  themes: [],
  products: [],
  isFetching: false,

  fetch: async () => {
    set({ themes: [], isFetching: true });

    try {
      const [, themes, products] = await Promise.all([
        get().dispatchThemes(),
        get().getThemes(),
        get().getProducts(),
      ]);

      set({
        products,
        themes: get().filterThemes(themes, products),
      });
    } finally {
      // Clear on failure too: useDesignsStore polls until this is false
      set({ isFetching: false });
    }

    get().dispatchThemes();

    return get().themes;
  },

  length: async () => {
    // Fetch the themes if they don't exist
    const themes = get().themes.length > 0 ? get().themes : await get().fetch();

    return themes.length;
  },

  some: async () => (await get().length()) > 0,

  filterThemes: (themes, products) => {
    // Return all if it's not a card
    if (!isCard()) return get().orderByBestSeller(themes);

    const filters = get().getCurrentProductFilters(products);

    const filtered = themes.filter((theme) =>
      // return true if one or more of the filters matches
      // the current product
      get()
        .getProductFiltersByDesignCode(products, theme.design_code)
        .some((f) => filters.includes(f)),
    );

    // If there are no matches then return them all
    return filtered.length > 0 ? get().orderByBestSeller(filtered) : themes;
  },

  orderByBestSeller: (items) =>
    items.sort((a, b) => (a.sales_total < b.sales_total ? 1 : -1)),

  getThemes: async () => {
    const response = await fetch(wkData.themes_url, {
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error(response.statusText);

    const { themes } = await response.json();
    return themes || [];
  },

  getProducts: async () => {
    const url = new URL(wkData.info_url);
    const path = url.pathname;
    const response = await fetch(`${path}/themes.json`, {
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error(response.statusText);

    const { products } = await response.json();
    return products || [];
  },

  dispatchThemes: async () =>
    builderDispatch.delayedDispatch({
      type: PrismUI.THEMES_LOADED,
      themes: get().themes,
    }),

  getProductFiltersByDesignCode: (products, code) =>
    productFiltersByDesignCode(products, code),

  getCurrentProductFilters: (products) => {
    // Get a list of filters for the current prism item
    // but exclude any that we're not interested in
    const prismItem = usePrismItemStore.getState().prismItem;

    return get()
      .getProductFiltersByDesignCode(products, prismItem.design_code)
      .filter((filter) =>
        cardFilters.some((cf) => filter === cf || filter.startsWith(`${cf}:`)),
      );
  },

  /**
   * Returns occasion-relevant filter tokens (top-level keys + compound
   * "key:value" tokens) for the currently active design. Only includes tokens
   * whose key is in the cardFilters allowlist so that non-occasion attributes
   * like "size", "paper", "colour" do not pollute the match.
   */
  getOccasionFilters: () => {
    if (get().products.length === 0) return [];
    const prismItem = usePrismItemStore.getState().prismItem;
    return get()
      .getProductFiltersByDesignCode(get().products, prismItem.design_code)
      .filter((f) =>
        cardFilters.some((cf) => f === cf || f.startsWith(`${cf}:`)),
      );
  },

  /**
   * Returns filter tokens for any design_code using the cached products data.
   */
  getFiltersByDesignCode: (designCode) =>
    get().getProductFiltersByDesignCode(get().products, designCode),
}));
